// The shared trip's itinerary: the index child of /shared/trip/:shareId.
// TripGroup's table read-only -- same columns, same note continuation rows,
// no Order / Details / Delete / Add leg anywhere. A row (or a leg on the map)
// opens the read-only leg view; everything it needs is already in the Outlet
// context, so that navigation costs nothing.

import { Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useNavigate } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { DataCard, Spinner } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import { urlForSharedTripLeg } from '../../fleet-shared/Urls.mjs';
import { tripGroupDateRange, tripGroupSummary } from '../../fleet-shared/TripGroups.mjs';
import { routeFromTrip, hasPlacedWaypoints } from '../../fleet-shared/TripRoute.mjs';
import { useSharedTrip, sharedTripColumns, sharedCellValue } from './SharedTrip';

// Lazy: the only import path here that pulls in maplibre-gl (~250 KB gz).
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

function legNote(leg) {
  return typeof leg?.notes === 'string' ? leg.notes.trim() : '';
}

const SharedTripItinerary = () => {
  const navigate = useNavigate();
  const { shareId, name, group, displayLegs, placeIndex } = useSharedTrip();

  const onDetails = (leg) => navigate(urlForSharedTripLeg(shareId, leg.id));

  const anyLegOnMap = (displayLegs ?? []).some((leg) =>
    hasPlacedWaypoints(routeFromTrip(leg))
  );

  return (
    <>
      <DataCard
        header={<CardHeader text={name} />}
        body={
          <>
            {group && (
              <div className="text-center text-body-secondary mb-2">
                <div>{tripGroupDateRange(group)}</div>
                {tripGroupSummary(group, { placeIndex }) && (
                  <div className="small">{tripGroupSummary(group, { placeIndex })}</div>
                )}
              </div>
            )}

            <Table responsive hover className="align-middle mb-0">
              <thead className="bg-body-tertiary">
                <tr>
                  {sharedTripColumns.map((field) => (
                    <th key={field.key} scope="col">{field.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(displayLegs ?? []).map((leg) => {
                  const note = legNote(leg);
                  return (
                    <SharedLegRow
                      key={leg.id}
                      leg={leg}
                      note={note}
                      placeIndex={placeIndex}
                      onDetails={onDetails}
                    />
                  );
                })}
              </tbody>
            </Table>
          </>
        }
      />

      {anyLegOnMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap legs={displayLegs} onSelectLeg={onDetails} />
        </Suspense>
      )}
    </>
  );
};

// One leg as one or two <tr>s, TripGroup's leg-row / leg-note-row pairing
// (App.css) with the control columns gone.
const SharedLegRow = ({ leg, note, placeIndex, onDetails }) => (
  <>
    <tr
      onClick={() => onDetails(leg)}
      role="button"
      style={{ cursor: 'pointer' }}
      className={note ? 'leg-row' : undefined}
    >
      {sharedTripColumns.map((field) => {
        const { text, title } = sharedCellValue(leg, field, placeIndex);
        return (
          <td key={field.key} className="align-middle entered-data" title={title}>{text}</td>
        );
      })}
    </tr>
    {note && (
      <tr
        onClick={() => onDetails(leg)}
        role="button"
        style={{ cursor: 'pointer' }}
        className="leg-note-row"
      >
        <td
          colSpan={sharedTripColumns.length}
          className="small text-body-secondary pt-0"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {note}
        </td>
      </tr>
    )}
  </>
);

export default SharedTripItinerary;
